import { useEffect, useRef, useState } from "react";

// A long list built a part at a time: the first `initial` rows, then `step`
// more whenever its end comes near the scroll container's view, so opening a
// big group's member list doesn't build (and re-render) every row at once.
// Returns [count, scrollRef, sentinelRef]: render the first `count` rows, put
// scrollRef on the element that scrolls and sentinelRef on an element after
// the rendered rows while count < total. Without IntersectionObserver, every
// row is rendered. A new resetKey (e.g. a search) starts again from the first
// rows.
export default function useProgressiveList(total, initial = 60, step = 200, resetKey) {
  const [count, setCount] = useState(initial);
  useEffect(() => {
    setCount(initial);
  }, [resetKey, initial]);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const supported = typeof IntersectionObserver !== "undefined";

  useEffect(() => {
    if (!supported || count >= total || !sentinelRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCount((current) => Math.min(total, current + step));
        }
      },
      { root: scrollRef.current, rootMargin: "600px 0px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [supported, count, total, step]);

  return [supported ? Math.min(count, total) : total, scrollRef, sentinelRef];
}
